<template>
  <div class="validation-mini-card">
    <div class="validation-mini-card__left">
      <div class="validation-mini-card__name" :title="sessionName">
        {{ sessionName }}
      </div>
      <div class="validation-mini-card__uid">
        <EncodedIdField
          :id="sessionId"
          :type="'validation'"
          :hideLabel="true"
        />
      </div>
    </div>

    <div class="validation-mini-card__platform-block">
      <div class="validation-mini-card__col2">
        <v-tooltip v-if="platformImageSrc" location="right" :open-delay="80" content-class="validation-mini-card-image-tooltip">
          <template #activator="{ props: imageTipProps }">
            <div class="validation-mini-card__image-wrap" v-bind="imageTipProps">
              <img
                :src="platformImageSrc"
                :alt="platformName"
                class="validation-mini-card__platform-image"
              >
            </div>
          </template>

          <img
            :src="platformImageSrc"
            :alt="platformName"
            class="validation-mini-card__image-preview-img"
          >
        </v-tooltip>

        <div v-else class="validation-mini-card__platform-placeholder">No Image</div>
      </div>

      <v-tooltip location="top" :open-delay="120" content-class="validation-mini-card-tooltip" :disabled="platformTooltipEntries.length === 0">
        <template #activator="{ props: tipProps }">
          <div class="validation-mini-card__col3" v-bind="tipProps">
            <div class="validation-mini-card__platform-name">
              {{ platformName }}
            </div>
            <div class="validation-mini-card__platform-host">
              {{ platformHost }}
            </div>
          </div>
        </template>

        <div v-if="platformTooltipEntries.length > 0" class="validation-mini-card__tooltip-content">
          <div
            v-for="entry in platformTooltipEntries"
            :key="entry.key"
            class="validation-mini-card__tooltip-row"
          >
            <span class="validation-mini-card__tooltip-key">{{ entry.key }}:</span>
            <span class="validation-mini-card__tooltip-value">{{ entry.value }}</span>
          </div>
        </div>
      </v-tooltip>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import EncodedIdField from '@/components/EncodedIdField.vue';

const props = defineProps({
  session: {
    type: Object,
    default: () => ({}),
  },
});

const sessionId = computed(() => Number(
  props.session?.id
  || props.session?.validate_session_id
  || props.session?.val_session_id
  || 0,
));

const sessionName = computed(() => (
  props.session?.docker_task?.name
  || props.session?.name
  || props.session?.title
  || `Validation ${sessionId.value || '-'}`
));

const platformInstance = computed(() => (
  props.session?.platform_instance
  || props.session?.params?.platform_instance
  || props.session?.platform
  || {}
));

const platformName = computed(() => (
  platformInstance.value?.name
  || platformInstance.value?.platform_name
  || platformInstance.value?.platform
  || props.session?.platform_name
  || props.session?.params?.platform_name
  || 'Platform (N/A)'
));

const platformHost = computed(() => (
  platformInstance.value?.host
  || platformInstance.value?.host_name
  || platformInstance.value?.hostname
  || platformInstance.value?.ip
  || platformInstance.value?.address
  || 'Host (N/A)'
));

const platformImageSrc = computed(() => {
  const direct = String(
    platformInstance.value?.image
    || platformInstance.value?.image_url
    || platformInstance.value?.imageUrl
    || platformInstance.value?.logo
    || platformInstance.value?.logo_url
    || platformInstance.value?.logoUrl
    || props.session?.platform_image
    || props.session?.platform_logo
    || '',
  ).trim();

  if (direct) {
    return direct;
  }

  const platformCode = String(
    platformInstance.value?.code
    || platformInstance.value?.platform_code
    || props.session?.platform_code
    || '',
  ).trim();

  return platformCode ? `/platform/${encodeURIComponent(platformCode)}/image` : '';
});

const platformTooltipEntries = computed(() => {
  const source = platformInstance.value;
  if (!source || typeof source !== 'object') {
    return [];
  }

  return Object.entries(source)
    .filter(([key, value]) => key && value !== undefined && value !== null && String(value).trim() !== '')
    .map(([key, value]) => ({
      key,
      value: typeof value === 'object' ? JSON.stringify(value) : String(value),
    }));
});

</script>

<style lang="scss" scoped>
.validation-mini-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 5px 8px;
  background: #ffffff;
}

.validation-mini-card__left {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.validation-mini-card__name {
  font-size: 0.84rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.validation-mini-card__uid {
  font-size: 0.72rem;
}

.validation-mini-card__platform-block {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  cursor: default;
}

.validation-mini-card__col2 {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
}

.validation-mini-card__image-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

.validation-mini-card__col3 {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.validation-mini-card__platform-image {
  width: 48px;
  height: 48px;
  object-fit: contain;
  border-radius: 0;
  border: none;
  background: transparent;
}

.validation-mini-card__platform-placeholder {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  border: 1px dashed #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5rem;
  color: #64748b;
}

.validation-mini-card__image-preview-img {
  width: 260px;
  height: 260px;
  object-fit: contain;
}

.validation-mini-card__platform-name {
  font-size: 0.66rem;
  color: #374151;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.validation-mini-card__platform-host {
  font-size: 0.6rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.validation-mini-card__tooltip-content {
  min-width: 220px;
  max-width: 320px;
  padding: 8px;
}

:deep(.validation-mini-card-tooltip) {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  color: #1f2937;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

:deep(.validation-mini-card-image-tooltip) {
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.validation-mini-card__tooltip-row {
  display: grid;
  grid-template-columns: minmax(80px, auto) minmax(0, 1fr);
  gap: 6px;
  font-size: 0.68rem;
  line-height: 1.3;
}

.validation-mini-card__tooltip-row + .validation-mini-card__tooltip-row {
  margin-top: 4px;
}

.validation-mini-card__tooltip-key {
  font-weight: 700;
  color: #334155;
}

.validation-mini-card__tooltip-value {
  color: #475569;
  word-break: break-word;
}

@media (max-width: 900px) {
  .validation-mini-card {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}
</style>
